import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { Upload, CheckCircle, XCircle, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};
export interface FileUploadItem {
  file: File;
  status: 'waiting' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}
interface FileUploadModalProps {
  isOpen: boolean;
  files: FileUploadItem[];
  onClose: () => void;
  onRemoveFile?: (index: number) => void;
  autoCloseCountdown?: number; // Auto-close countdown in seconds (0 means no auto-close)
}
export default function FileUploadModal({
  isOpen,
  files,
  onClose,
  onRemoveFile,
  autoCloseCountdown = 0,
}: FileUploadModalProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [totalProgress, setTotalProgress] = useState(0);
  // Calculate total progress
  useEffect(() => {
    if (files.length === 0) {
      setTotalProgress(0);
      return;
    }
    const completedFiles = files.filter(
      f => f.status === 'success' || f.status === 'error'
    ).length;
    const uploadingFiles = files.filter(f => f.status === 'uploading');
    const uploadingProgress = uploadingFiles.reduce(
      (sum, f) => sum + f.progress,
      0
    );
    const total = (completedFiles * 100 + uploadingProgress) / files.length;
    setTotalProgress(Math.round(total));
  }, [files]);
  const allCompleted =
    files.length > 0 &&
    files.every(f => f.status === 'success' || f.status === 'error');
  const hasErrors = files.some(f => f.status === 'error');
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  if (!isOpen) return null;
  const getStatusIcon = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'waiting':
        return <Clock size={16} className="text-gray-400" />;
      case 'uploading':
        return (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
        );
      case 'success':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'error':
        return <XCircle size={16} className="text-red-500" />;
    }
  };
  const getStatusText = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'waiting':
        return t('common.waiting');
      case 'uploading':
        return t('common.uploading');
      case 'success':
        return t('common.success');
      case 'error':
        return t('common.failed');
    }
  };
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return `0 ${t('fileSize.bytes')}`;
    const k = 1024;
    const sizes = [
      t('fileSize.bytes'),
      t('fileSize.kb'),
      t('fileSize.mb'),
      t('fileSize.gb'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Upload size={20} className="text-blue-500" />
            <div>
              <DialogTitle>{t('dialog.fileUploadProgress')}</DialogTitle>
              {allCompleted && (
                <p className="text-sm text-gray-600 mt-1">
                  {successCount} {t('dialog.successful')}, {errorCount}{' '}
                  {t('dialog.failed')}
                </p>
              )}
            </div>
          </div>
        </DialogHeader>
        {/* Overall Progress */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t('toast.overallProgress')}
            </span>
            <span className="text-sm text-gray-600">{totalProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                hasErrors && allCompleted ? 'bg-red-500' : 'bg-blue-500'
              )}
              style={{ width: `${totalProgress}%` }}
            />
          </div>
        </div>
        {/* File List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {files.map((fileItem, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg"
              >
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {getStatusIcon(fileItem.status)}
                </div>
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.file.name}
                    </h4>
                    <span className="text-xs text-gray-500 ml-2">
                      {formatFileSize(fileItem.file.size)}
                    </span>
                  </div>
                  {/* Progress Bar for uploading files */}
                  {fileItem.status === 'uploading' && (
                    <div className="mb-2">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  {/* Status Text */}
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'text-xs font-medium',
                        fileItem.status === 'success' && 'text-green-600',
                        fileItem.status === 'error' && 'text-red-600',
                        fileItem.status === 'uploading' && 'text-blue-600',
                        fileItem.status === 'waiting' && 'text-gray-600'
                      )}
                    >
                      {getStatusText(fileItem.status)}
                      {fileItem.status === 'uploading' &&
                        ` (${fileItem.progress}%)`}
                    </span>
                  </div>
                  {/* Error Message */}
                  {fileItem.status === 'error' && fileItem.error && (
                    <p className="text-xs text-red-600 mt-1">
                      {fileItem.error}
                    </p>
                  )}
                </div>
                {/* Remove Button */}
                {onRemoveFile &&
                  (fileItem.status === 'waiting' ||
                    fileItem.status === 'error') && (
                    <button
                      onClick={() => onRemoveFile(index)}
                      className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                      title={t('tooltips.removeFile')}
                    >
                      <X size={16} className="text-gray-400" />
                    </button>
                  )}
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        {allCompleted && (
          <DialogFooter className="flex-row justify-between items-center">
            {/* Auto-close countdown */}
            {autoCloseCountdown > 0 && !hasErrors && (
              <div className="text-sm text-gray-500">
                {t('toast.autoClosingIn', { seconds: autoCloseCountdown })}
              </div>
            )}
            <div className="flex-1"></div>
            <Button variant="outline" onClick={onClose}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
